// src/components/Layout.js
// Org-level sponsor feature gates (Trial Experience, ChatDiligent) are enforced in:
// ProtectedRoute, Sidebar, App floating chat, discovery, provider list/card — see utils/orgFeatureGates.js
// (reads Redux `loggedInUser.userDetails` from setLoggedInUserDetails / sponsor profile).

import React, { useEffect, useState } from "react";
import Sidebar from "./Sidebar";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router-dom";
import { useRfiListForFilters } from "../custom-hooks/useFiltersData-hooks";
import { setRFIs } from "../redux-store/features/rfisColors";
import { useAuth, useClerk, useUser } from "@clerk/clerk-react";
import { ErrorBoundary } from "@sentry/react";
import Notification from "./Notification";
import RouteErrorFallback from "./RouteErrorFallback";
import { useResettableErrorBoundaryKey } from "../custom-hooks/useResettableErrorBoundaryKey";
import {
  useCheckEmailConfirmed,
  useGetClientSecrets,
  useGetVendorsMe,
  useLoggedInGetSponsorUserDetails,
} from "../custom-hooks/queries";
import { useUserRole } from "../utils/helperMethods";
import { setLoggedInUserDetails } from "../redux-store/features/loggedInUserSlice";
import { setVendorUserDetails } from "../redux-store/features/vendorUserDetailsSlice";
import { setClientSecrets } from "../redux-store/features/clientSecretsSlice";

const Layout = ({ children }) => {
  const dispatch = useDispatch();
  const location = useLocation();
  const { boundaryKey: contentBoundaryKey, onError: onContentError } =
    useResettableErrorBoundaryKey(location.pathname);
  const { user, isLoaded } = useUser();
  const role = user?.publicMetadata?.role;
  const isDrawerOpen = useSelector((state) => state.layout.drawerOpen);
  const { data: clientSecrets } = useGetClientSecrets();
  const { rfiListForFilters } = useRfiListForFilters();
  const { data: userDetails, isLoading: loads } =
    useLoggedInGetSponsorUserDetails(role);
  const { data: vendorUser } = useGetVendorsMe(role);

  const { isSignedIn, isLoaded: isAuthLoaded } = useAuth();

  useEffect(() => {
    // Wait for Clerk to finish loading so we don't wipe sticky filters during the
    // brief unsigned flash on refresh for the same user.
    if (!isAuthLoaded) {
      return;
    }

    if (!isSignedIn) {
      sessionStorage.clear();
    }
  }, [isAuthLoaded, isSignedIn]);

  useEffect(() => {
    if (userDetails) {
      dispatch(setLoggedInUserDetails(userDetails));
    }
  }, [userDetails, dispatch]);

  useEffect(() => {
    if (vendorUser) {
      dispatch(setVendorUserDetails(vendorUser));
    }
  }, [vendorUser, dispatch]);

  useEffect(() => {
    if (clientSecrets && rfiListForFilters) {
      dispatch(setClientSecrets(clientSecrets));
      dispatch(setRFIs(rfiListForFilters));
    }
  }, [rfiListForFilters, clientSecrets, dispatch]);

  const [showModal, setShowModal] = useState(false);
  const [message, setMessage] = useState("");
  const userRole = useUserRole();

  const { data, isLoading } = useCheckEmailConfirmed(role);

  const { signOut } = useClerk();

  // useEffect(() => {
  //   if (isLoading || data === undefined) return;

  //   if (["Backend User", "Backend Master User"].includes(role)) {
  //     const isVerified = data;
  //     if (isVerified === true) {
  //     } else {
  //       setMessage(
  //         "Your email is not verified. Please verify your email to continue."
  //       );
  //       setShowModal(true);
  //       setTimeout(() => {
  //         signOut();
  //       }, 2000);
  //     }
  //   }
  // }, [data]);

  return (
    <div
      className={`layout ${
        isDrawerOpen ? "backdrop-blur-sm backdrop-filter" : ""
      }`}
    >
      <Sidebar userDetails={userDetails} vendorUserRights={vendorUser} />
      <Notification
        show={showModal}
        setShow={setShowModal}
        status={"error"}
        errorMessage={message}
      />
      <main className="ml-0 min-w-0 lg:ml-72">
        <div className="mx-auto w-full min-w-0 max-w-full">
          <main className="flex w-full min-w-0 flex-wrap justify-center">
            <ErrorBoundary
              key={contentBoundaryKey}
              onError={onContentError}
              fallback={RouteErrorFallback}
            >
              {children}
            </ErrorBoundary>
          </main>
        </div>
      </main>
    </div>
  );
};

export default Layout;
